// Pure policy for PROACTIVE access-token refresh (used by auth.js).
//
// The access token used to be minted once and refreshed only REACTIVELY, on a 401.
// A long agent run therefore outlived its token: API calls started 401ing and the
// stream died mid-flight, and only a full page reload re-authed. These helpers decide
// when to refresh before expiry, and how to survive the multi-tab rotation race.

// Refresh at half the token's remaining life: early enough that a stalled tab still
// has a valid token, late enough that we are not hammering /api/auth/refresh.
const REFRESH_FRACTION = 0.5;
// Floor keeps a nearly-expired token from scheduling a hot loop.
const MIN_DELAY_MS = 1_000;
// Ceiling so a long-lived token still gets a periodic refresh rather than sitting
// untouched for hours (and so a suspended laptop wakes into a fresh-ish token).
export const MAX_DELAY_MS = 15 * 60_000;

/** Seconds-since-epoch `exp` from a JWT, or null when it can't be read. */
export function decodeJwtExp(token) {
  if (typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = typeof atob === "function"
      ? atob(b64)
      : Buffer.from(b64, "base64").toString("binary");
    const exp = JSON.parse(json)?.exp;
    return typeof exp === "number" && Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
}

/**
 * Milliseconds to wait before proactively refreshing `token`.
 * null  -> cannot schedule (no/unreadable token); fall back to reactive refresh.
 * 0     -> already expired (or expiring now); refresh immediately.
 * Never returns a delay that lands after the token expires.
 */
export function refreshDelayMs(token, nowMs) {
  const exp = decodeJwtExp(token);
  if (exp == null) return null;
  const ttl = exp * 1000 - nowMs;
  if (ttl <= 0) return 0;
  return Math.min(Math.max(ttl * REFRESH_FRACTION, MIN_DELAY_MS), MAX_DELAY_MS, ttl);
}

/**
 * Whether a FAILED refresh is worth one more immediate attempt.
 *
 * Refresh tokens are single-use, so two tabs presenting the same cookie race: the
 * loser gets a 401 even though the winner has just installed a perfectly valid
 * cookie. Without a retry that tab drifts into "session expired - please sign in
 * again" while a working credential sits in the jar, and only a reload recovers it.
 */
export function shouldRetryRefresh(attempt) {
  return attempt < 1;
}
